import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useConfigStore } from '@renderer/stores/configState'
import { useHubStore } from '@renderer/stores/hubStore'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useCountdown } from '@renderer/utils/countdownUtils'
import { formatAmount } from '@renderer/utils/formatCurrency'
import { getActiveHubMembers } from '@renderer/utils/navigationUtils'
import { useDesignLayout } from '@renderer/hooks/useDesignLayout'
import { useCachedImage } from '@renderer/hooks/useCachedImage'
import { BackgroundContainer } from '@renderer/components/BackgroundContainer'
import type { ModeSelectMember } from '@renderer/types/configState'
import { HUB_V1_SUPPORTED_CUSTOM_PATHS } from '@renderer/utils/hubSupport'

/**
 * Hub "Pilih Mode" page (`/hub/{hubId}/mode-select`).
 *
 * Runs BEFORE a session exists, like `tutorial`: the hub Welcome sends the
 * guest here instead of creating a session, the guest picks a member project,
 * and the pick is recorded in the persisted hub store — not in the URL, so a
 * reload cannot re-trigger the auto-start. Everything after the pick is the
 * member's untouched flow (its Checkout charges the member's price; this card
 * never becomes a source of truth for payment).
 */

/** A member whose page set the booth cannot hand off into yet (plan D6). */
function isUnsupportedMember(member: ModeSelectMember): boolean {
  const customPath = member.resolved?.customPath ?? null
  if (!HUB_V1_SUPPORTED_CUSTOM_PATHS.includes(customPath)) return true
  return member.resolved?.isActive === false
}

const MemberCard: React.FC<{
  member: ModeSelectMember
  currency: string | undefined
  disabled: boolean
  compact: boolean
  onPick: () => void
}> = ({ member, currency, disabled, compact, onPick }) => {
  const { t } = useTranslation()
  const thumbnailUrl = member.thumbnailUrl || member.resolved?.thumbnail || ''
  const cachedThumb = useCachedImage(thumbnailUrl)
  const price = member.resolved?.startingPrice
  const priceCurrency = member.resolved?.currency || currency

  return (
    <button
      type="button"
      onClick={onPick}
      disabled={disabled}
      data-mode-member={member.projectId}
      className={`group flex min-h-0 min-w-0 flex-col overflow-hidden rounded-3xl border-4 bg-white text-left shadow-lg transition-transform ${
        disabled
          ? 'cursor-not-allowed border-gray-200 opacity-50'
          : 'border-transparent hover:border-blue-500 active:scale-[0.98]'
      }`}
    >
      <div className="relative w-full flex-1 min-h-0 bg-gray-100">
        {thumbnailUrl ? (
          <img
            src={cachedThumb || thumbnailUrl}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-6xl text-gray-300">
            {member.label.slice(0, 1).toUpperCase()}
          </div>
        )}
      </div>
      <div className={`flex flex-col gap-1 ${compact ? 'p-4' : 'p-6'}`}>
        <span className={`font-bold text-gray-900 ${compact ? 'text-2xl' : 'text-4xl'}`}>
          {member.label}
        </span>
        {member.description && (
          <span className={`text-gray-600 ${compact ? 'text-base' : 'text-xl'}`}>
            {member.description}
          </span>
        )}
        {disabled ? (
          <span className={`mt-2 font-semibold text-red-600 ${compact ? 'text-sm' : 'text-lg'}`}>
            {t('modeSelect.unavailable', 'Not available on this booth')}
          </span>
        ) : typeof price === 'number' ? (
          <span className={`mt-2 font-semibold text-blue-700 ${compact ? 'text-lg' : 'text-2xl'}`}>
            {t('modeSelect.from', 'from {{price}}', {
              price: formatAmount(price, priceCurrency)
            })}
          </span>
        ) : null}
      </div>
    </button>
  )
}

const ModeSelectPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { hubId: routeHubId } = useParams()
  const { config } = useConfigStore()
  const { logActivity } = useSessionStateStore()
  const enter = useHubStore((s) => s.enter)
  const { isCompact, isPortrait } = useDesignLayout()
  const [picked, setPicked] = useState<string | null>(null)

  const settings = config?.pages.modeSelect?.settings
  const members = useMemo(() => getActiveHubMembers(config), [config])
  const timeoutSeconds = Math.max(0, Math.floor(settings?.modeSelectTimeout ?? 0))
  // `config._id` IS the hub id under `hubToConfig`; the route param is the
  // fallback for the frame between a config swap and this page re-rendering.
  const hubId = config?._id || routeHubId

  const goHome = (): void => {
    navigate(`/hub/${hubId}`)
  }

  // Timeout → back to the hub Welcome, same as every pre-session page.
  const countdown = useCountdown(timeoutSeconds)
  useEffect(() => {
    if (timeoutSeconds <= 0) return
    countdown.setOnComplete(goHome)
    countdown.start()
    return (): void => countdown.stop()
  }, [timeoutSeconds, hubId])

  const handlePick = (member: ModeSelectMember): void => {
    if (picked || !hubId) return
    setPicked(member.projectId)
    // No session exists yet, so this log line is dropped by the store — the
    // member's `startSession` logs the real start. Kept for the dev console.
    logActivity(`Hub mode picked: ${member.label}`, { memberProjectId: member.projectId })
    enter(hubId, member.projectId, { returnTo: settings?.returnTo })
    navigate(`/${member.projectId}`)
  }

  const title = settings?.title?.trim() || t('modeSelect.title', 'Which photo do you want?')
  const subtitle =
    settings?.subtitle?.trim() ||
    t('modeSelect.subtitle', 'Each mode has its own packages, frames and print size.')
  /**
   * Columns for the card grid. This used to be a row/column switch, which was
   * fine for three modes and unusable at ten — every card got a tenth of the
   * screen height. A portrait booth gets fewer columns than a landscape one at
   * the same count, because its cards are taller than they are wide.
   *
   * Written as an inline style, not `grid-cols-${n}`: Tailwind only emits the
   * classes it can see in the source, so a computed one is purged and the grid
   * silently collapses to a single column.
   */
  const columnCount = ((): number => {
    const n = members.length
    if (n <= 1) return 1
    if (isPortrait) {
      if (n <= 4) return 2
      return 3
    }
    if (n <= 3) return n
    if (n <= 8) return 4
    return 5
  })()

  return (
    <BackgroundContainer
      className="relative flex h-full w-full flex-col overflow-hidden"
      data-page="mode-select"
    >
      <div className={`flex flex-col items-center text-center ${isCompact ? 'px-6 pt-8' : 'px-12 pt-14'}`}>
        <h1 className={`font-bold text-gray-900 ${isCompact ? 'text-3xl' : 'text-6xl'}`}>{title}</h1>
        <p className={`mt-2 text-gray-600 ${isCompact ? 'text-base' : 'text-2xl'}`}>{subtitle}</p>
      </div>
      <div
        className={`grid min-h-0 flex-1 ${isCompact ? 'gap-3 p-6' : 'gap-6 p-12'}`}
        style={{
          gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
          gridAutoRows: 'minmax(0, 1fr)'
        }}
        data-mode-count={members.length}
        data-mode-columns={columnCount}
      >
        {members.map((member) => (
          <MemberCard
            key={member.projectId}
            member={member}
            currency={config?.resolvedCurrency}
            disabled={isUnsupportedMember(member) || (picked !== null && picked !== member.projectId)}
            compact={isCompact}
            onPick={() => handlePick(member)}
          />
        ))}
      </div>
      <div className={`flex items-center justify-between ${isCompact ? 'px-6 pb-6' : 'px-12 pb-10'}`}>
        <button
          type="button"
          onClick={goHome}
          className={`rounded-full bg-gray-200 font-semibold text-gray-800 hover:bg-gray-300 ${
            isCompact ? 'px-6 py-3 text-base' : 'px-10 py-4 text-2xl'
          }`}
        >
          {t('common.back', 'Back')}
        </button>
        {settings?.ctaLabel?.trim() && (
          <span className={`text-gray-500 ${isCompact ? 'text-sm' : 'text-xl'}`}>
            {settings.ctaLabel}
          </span>
        )}
      </div>
    </BackgroundContainer>
  )
}

export default ModeSelectPage
